import { LayoutHeader, LayoutPage } from '@shinetools/lumen-react'
import debounce from 'lodash/debounce'
import { ReactElement, useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Navigate, useNavigate } from 'react-router'

import { useFetchCountries } from '@features/countries/hooks/useFetchCountries'
import { useUserOrganization } from '@modules-deprecated/app/organization'
import { useFetchContacts } from '@views/contacts/hooks/useFetchContacts'
import { useIsContactsListEmpty } from '@views/contacts/hooks/useIsContactsListEmpty'

import { useOrganizationViewUrl } from '../../hooks/routing/useOrganizationViewUrl'
import { getOrganizationViewUrl } from '../../utils/routing/getOrganizationViewUrl'
import { reactRoute } from '../../utils/routing/reactRoute'
import { SuppliersAddMenu } from './elements/SuppliersAddMenu'
import { SuppliersTable, SuppliersTableSortCriterion } from './elements/SuppliersTable'
import { defaultSuppliersTableColumnConfiguration } from './elements/SuppliersTable/constants/suppliersTableColumnConfigurations'
import type { SuppliersTableRow } from './elements/SuppliersTable/types/suppliersTableRow'
import { formatContactsToSuppliersTableData } from './elements/SuppliersTable/utils/formatContactsToSuppliersTableData'
import { SupplierDrawer } from './routes/SupplierDetail/elements/HeaderSupplierDetail/elements/SupplierDrawer'
import { transformSuppliersTableSortToContactsApiSort } from './utils/transformSuppliersTableSortToContactsApiSort'

const SEARCH_MINIMUM_CHARACTER_LENGTH = 3
const SEARCH_DEBOUNCE_DELAY_MS = 250
const PAGE_SIZE_OPTIONS = [10, 25, 50, 100] as const

export const SuppliersListRevamped = (): ReactElement | null => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { organization } = useUserOrganization()
  const [isCreateDrawerOpen, setIsCreateDrawerOpen] = useState(false)
  const [searchValue, setSearchValue] = useState('')
  const [debouncedSearchValue, setDebouncedSearchValue] = useState('')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [sortCriteria, setSortCriteria] = useState<SuppliersTableSortCriterion | null>({
    direction: 'ascending',
    key: 'name',
  })

  const { url: suppliersEmptyUrl } = useOrganizationViewUrl(reactRoute.organization.suppliers.getListEmptyRoute())
  const isContactsListEmpty = useIsContactsListEmpty({ isCustomer: false, isSupplier: true })
  const { countries } = useFetchCountries()

  const sorting = transformSuppliersTableSortToContactsApiSort(sortCriteria)
  const { contacts, isLoading, pagination } = useFetchContacts({
    isCustomer: false,
    isSupplier: true,
    page,
    pageSize,
    searchQuery: debouncedSearchValue.length >= SEARCH_MINIMUM_CHARACTER_LENGTH ? debouncedSearchValue : undefined,
    sortDirection: sorting?.sortDirection,
    sortProperty: sorting?.sortProperty,
  })

  const data = formatContactsToSuppliersTableData(contacts, countries)

  const updateDebouncedSearchValue = useMemo(
    () =>
      debounce((value: string) => {
        setDebouncedSearchValue(value)
      }, SEARCH_DEBOUNCE_DELAY_MS),
    [],
  )

  useEffect(() => () => updateDebouncedSearchValue.cancel(), [updateDebouncedSearchValue])

  const handleSearchChange = (newSearchValue: string) => {
    setPage(1)
    setSearchValue(newSearchValue)
    updateDebouncedSearchValue(newSearchValue)
  }

  const handlePageSizeChange = (newPageSize: number) => {
    setPage(1)
    setPageSize(newPageSize)
  }

  const handleCreateManuallySelect = () => {
    setIsCreateDrawerOpen(true)
  }

  const handleCreateDrawerClose = () => {
    setIsCreateDrawerOpen(false)
  }

  const handleRowClick = useCallback(
    (row: SuppliersTableRow) => {
      navigate(
        getOrganizationViewUrl(organization?.url, reactRoute.organization.suppliers.getSupplierDetailRoute(row.id)),
      )
    },
    [navigate, organization?.url],
  )

  if (isContactsListEmpty) {
    if (!suppliersEmptyUrl) {
      return null
    }

    return <Navigate replace to={suppliersEmptyUrl} />
  }

  return (
    <LayoutPage
      header={
        <LayoutHeader
          actions={<SuppliersAddMenu onCreateManuallySelect={handleCreateManuallySelect} />}
          title={t('navigation.menu.label.expenses.suppliers')}
        />
      }
      layout="list-table"
    >
      <SuppliersTable
        columns={defaultSuppliersTableColumnConfiguration}
        data={data}
        isLoading={isLoading}
        onPageChange={setPage}
        onPageSizeChange={handlePageSizeChange}
        onRowClick={handleRowClick}
        onSearchChange={handleSearchChange}
        onSortCriteriaChange={setSortCriteria}
        page={page}
        pageSize={pageSize}
        pageSizeOptions={PAGE_SIZE_OPTIONS}
        searchValue={searchValue}
        selectable
        sortCriteria={sortCriteria}
        totalCount={pagination?.total ?? 0}
      />
      <SupplierDrawer
        contact={null}
        isOpen={isCreateDrawerOpen}
        mode="create"
        onClose={handleCreateDrawerClose}
        title={t('suppliers.detail.create_supplier')}
      />
    </LayoutPage>
  )
}
